import {
    IQualificationConfig,
    IQualificationCombination,
    IQualificationOption,
} from '../services/nestjs-base-api/types';

export interface ResolveByCategoryResult {
    supported: boolean;
    needsModel: boolean;
    eligibleModels?: IQualificationOption[];
    templateType?: string;
}

export interface ResolveResult {
    supported: boolean;
    templateType?: string;
}

// Sentinel outcome for an entry that exists but is explicitly not supported yet
// (captured to the waitlist). Distinct from any real templateType.
const UNSUPPORTED = ' unsupported';

function isSupported(mapping: IQualificationCombination | undefined): boolean {
    return !!mapping?.templateType && mapping.supported !== false;
}

/**
 * Category-first resolver — mirror of the API's qualification-resolver.ts.
 * Scans a category across all business models and collects distinct OUTCOMES
 * (a templateType, or "captured to waitlist"):
 *  0 entries → not supported (waitlist), 1 distinct → resolve now (or waitlist),
 *  >1 → ask the model. A `supported: false` entry (e.g. Retail + perishable)
 *  counts as a distinct waitlist outcome, so the category asks the model.
 */
export function resolveByCategory(
    config: IQualificationConfig,
    category: string,
): ResolveByCategoryResult {
    const outcomeByModel = new Map<string, string>();

    Object.entries(config?.combinations || {}).forEach(([model, categories]) => {
        const mapping = categories?.[category];
        if (mapping !== undefined) {
            outcomeByModel.set(
                model,
                isSupported(mapping) ? (mapping.templateType as string) : UNSUPPORTED,
            );
        }
    });

    if (outcomeByModel.size === 0) {
        return { supported: false, needsModel: false };
    }

    const distinct = new Set(outcomeByModel.values());
    if (distinct.size === 1) {
        const only = Array.from(distinct)[0];
        if (only === UNSUPPORTED) {
            return { supported: false, needsModel: false };
        }
        return { supported: true, needsModel: false, templateType: only };
    }

    return {
        supported: true,
        needsModel: true,
        eligibleModels: (config.businessModels || []).filter((m) =>
            outcomeByModel.has(m.value),
        ),
    };
}

export function resolveByModelAndCategory(
    config: IQualificationConfig,
    businessModel: string,
    category: string,
): ResolveResult {
    const mapping = config?.combinations?.[businessModel]?.[category];
    if (isSupported(mapping)) {
        return { supported: true, templateType: mapping!.templateType };
    }
    return { supported: false };
}
